import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { I18nProvider } from "@/lib/i18n";
import { PageLocalesProvider } from "@/lib/pageLocales";
import { AuthProvider } from "@/lib/auth";
import Index from "./pages/Index.tsx";
import { ScrollToTop } from "./components/site/ScrollToTop";
import { DeferredMount } from "./components/site/DeferredMount";
import { LocaleGate, RootLocaleSync } from "./components/site/LocaleGate";
const ChatLauncher = lazy(() => import("./components/site/ChatLauncher"));

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const PhilosophyPage = lazy(() => import("./pages/ContentPages").then((m) => ({ default: m.PhilosophyPage })));
const ServiceAreasPage = lazy(() => import("./pages/ContentPages").then((m) => ({ default: m.ServiceAreasPage })));
const ServicesIndexPage = lazy(() => import("./pages/ContentPages").then((m) => ({ default: m.ServicesIndexPage })));
const ServiceDetailPage = lazy(() => import("./pages/ContentPages").then((m) => ({ default: m.ServiceDetailPage })));
const ContactPage = lazy(() => import("./pages/FormPages").then((m) => ({ default: m.ContactPage })));
const QuotePage = lazy(() => import("./pages/FormPages").then((m) => ({ default: m.QuotePage })));
const HrPage = lazy(() => import("./pages/FormPages").then((m) => ({ default: m.HrPage })));
const AuthPage = lazy(() => import("./pages/Auth"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Admin = lazy(() => import("./pages/Admin"));
const LocalLanding = lazy(() => import("./pages/LocalLanding"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 6,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

/** Inner route tree, used both for `/` (TR) and `/:locale/*` (en/de/es/ru/fr). */
const innerRoutes = (
  <>
    <Route index element={<Index />} />
    <Route path="felsefemiz" element={<PhilosophyPage />} />
    <Route path="hizmetlerimiz" element={<ServicesIndexPage />} />
    <Route path="hizmet/:slug" element={<ServiceDetailPage />} />
    {/* Eski kısa hizmet URL'leri → canonical /hizmet/:slug */}
    <Route path="koruma-ve-guvenlik" element={<Navigate to="../hizmet/koruma-ve-guvenlik" replace />} />
    <Route path="genel-temizlik" element={<Navigate to="../hizmet/genel-temizlik" replace />} />
    <Route path="zirhli-arac-ve-tasima" element={<Navigate to="../hizmet/zirhli-arac-ve-tasima" replace />} />
    <Route path="tesis-yonetimi" element={<Navigate to="../hizmet/tesis-yonetimi" replace />} />
    <Route path="ilaclama" element={<Navigate to="../hizmet/ilaclama" replace />} />
    {/* Kaldırılan peyzaj hizmeti → tesis yönetimi */}
    <Route path="bahce-bakim-ve-peyzaj" element={<Navigate to="../hizmet/tesis-yonetimi" replace />} />
    <Route path="hizmet/bahce-bakim-ve-peyzaj" element={<Navigate to="../hizmet/tesis-yonetimi" replace />} />
    <Route path="elektronik-guvenlik-sistemleri" element={<Navigate to="../hizmet/elektronik-guvenlik-sistemleri" replace />} />
    <Route path="hizmet-alanlarimiz" element={<Navigate to="../hizmetlerimiz" replace />} />
    <Route path="iletisim" element={<ContactPage />} />
    <Route path="teklif-iste" element={<QuotePage />} />
    <Route path="insan-kaynaklari" element={<HrPage />} />
    <Route path="giris" element={<AuthPage />} />
    <Route path="blog" element={<BlogIndex />} />
    <Route path="blog/:slug" element={<BlogPost />} />
    <Route path="admin" element={<Admin />} />
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <I18nProvider>
      <PageLocalesProvider>
        <AuthProvider>
          <DeferredMount>
            <Toaster />
            <Sonner />
          </DeferredMount>
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={null}>
              <Routes>
                {/* TR (varsayılan, prefix yok) */}
                <Route path="/" element={<RootLocaleSync />}>
                  {innerRoutes}
                  {/* İlçe × hizmet landing sayfaları — BİLEREK yalnızca TR ağacında.
                      innerRoutes'a konursa /en/hizmet/.../kadikoy gibi 7 dilde aynı
                      Türkçe metni gösteren kopya URL'ler doğar. Dil önekli istekler
                      nginx'te TR karşılığına 301'leniyor. */}
                  <Route path="hizmet/:slug/:bolge" element={<LocalLanding />} />
                </Route>
                {/* Diğer diller: /en, /de, /es, /ru, /fr */}
                <Route path="/:locale" element={<LocaleGate />}>
                  {innerRoutes}
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            {/* Chat asistanı: LCP / Speed-Index ölçüm penceresinin tamamen dışında
                kalsın diye en az 4 sn sonra mount edilir. */}
            <DeferredMount minDelayMs={4000}>
              <Suspense fallback={null}>
                <ChatLauncher />
              </Suspense>
            </DeferredMount>
          </BrowserRouter>
        </AuthProvider>
      </PageLocalesProvider>
    </I18nProvider>
  </QueryClientProvider>
);

export default App;
